import { Injectable } from "@angular/core";
import { filter, firstValueFrom, map, merge, Observable } from "rxjs";
import { ObAccountModel } from "../models/ob-account.model";
import { ObInstitutionModel } from "../models/ob-institution.model";
import { CreatePaymentRequest, ObPaymentAuthUrlRequest } from "../models/ob-payment.model";
import { BrowserService } from "./browser.service";
import { OpenBankingApi } from "./open-banking.api";

@Injectable({
  providedIn: 'root'
})
export class OpenBankingService {
  constructor(
    private openBankingApi: OpenBankingApi,
    private browserService: BrowserService
  ) {
  }

  getInstitutions$(): Observable<ObInstitutionModel[]> {
    return this.openBankingApi.obInstitutions$();
  }

  async getAccounts(consent: string, institutionId: number): Promise<ObAccountModel> {
    return await this.openBankingApi.obAccounts(consent, institutionId);
  }

  getCategories(accountId: number, consent: string) {
    return this.openBankingApi.obGetCategories(accountId, consent);
  }

  async getAffordability(accountId: number, consent: string, premium: number) {
    return await this.openBankingApi.obGetAffordability(accountId, consent, premium);
  }

  getTransactions(accountId: number, consent: string) {
    return this.openBankingApi.obGetTransactions(accountId, consent);
  }

  async getConsent(institutionId: number, userId: string) {
    const result = await this.openBankingApi.obAccountAuthUrl({ institutionId, userId });

    const obWindow = this.browserService.openWindow(result.authorizationUrl, '_blank');

    const obResult = await firstValueFrom(
      merge(
        obWindow.message$.pipe(
          filter(x => x.data.type === 'consent'),
          filter(x => x.origin === result.hostUrl),
          map(msg => ({ closed: false, consent: msg.data.consent })),
        ),
        obWindow.closed$.pipe(map(_ => ({ closed: true, consent: <string>null })))
      )
    );

    if (!obResult.closed) {
      obWindow.close();
    }

    return obResult.consent;
  }

  async getPaymentAuthUrl(institutionId: number, amount: number, accountId: number) {
    let request: ObPaymentAuthUrlRequest = {
      institutionId: institutionId,
      amount: amount,
      accountId: accountId
    };
    return await this.openBankingApi.obPaymentAuthUrl(request);
  }

  async createPayment(consent: string, authRequestId: number) {
    let request: CreatePaymentRequest = {
      consent: consent,
      authRequestId: authRequestId
    }
    return await this.openBankingApi.obPayment(request);
  }
}
